
import Vue from "vue";
import { Component, Prop } from "vue-property-decorator";
import {
  CustomTypographyInputValue,
  TypographyInputValue,
  TypographyPreset
} from "@/types/typography";
import ButtonBarInput from "@/components/Reusable/CustomInputs/ButtonBarInput/ButtonBarInput.vue";
import GlobalTypographySaveDialog from "@/components/Reusable/CustomInputs/Typography/GlobalTypographySaveDialog.vue";
import CustomTypographyInput from "@/components/Reusable/CustomInputs/Typography/CustomTypographyInput.vue";
import TypographyPresetChooserInput from "@/components/Reusable/CustomInputs/Typography/TypographyPresetChooserInput.vue";
import { globalStore } from "@/store/store-accessor";
import defensiveCopy from "@/util/defensive-copy";
import {
  decorateValueEmittedByInputWithMetadata,
  TypographyOptions,
  ValueEmittedFromControlInputDecoratedWithMetadata
} from "@/types/controls";
import NiceButton from "@/components/UI/NiceButton.vue";
import { openUiPanel } from "@/lib/panels/panels";
import { setGlobalSettingsAccordionState } from "@/lib/panels/accordions";

@Component({
  components: {
    NiceButton,
    TypographyPresetChooserInput,
    CustomTypographyInput,
    GlobalTypographySaveDialog,
    ButtonBarInput,
    ControlsTree: () =>
      import("@/components/Reusable/ControlsTree/ControlsTree.vue")
  }
})
export default class TypographyInput extends Vue {
  @Prop({
    default: (): TypographyInputValue => {
      return {
        custom: {
          customTypography: {}
        }
      };
    }
  })
  value!: TypographyInputValue; // well, this could actually be null, right?

  @Prop({
    default: () => {
      return {};
    }
  })
  options!: TypographyOptions;

  savePresetDialogOpen = false;

  emit(
    newValue:
      | TypographyInputValue
      | ValueEmittedFromControlInputDecoratedWithMetadata<TypographyInputValue>
  ) {
    this.$emit("input", defensiveCopy(newValue));
  }

  get mode() {
    return "preset" in this.value ? "global" : "custom";
  }

  updateMode(maybeNewMode: "custom" | "global") {
    const previousMode = this.mode;
    const newMode = maybeNewMode || "custom";

    if (previousMode === "global" && newMode === "custom") {
      const presetId = "preset" in this.value ? this.value.preset : undefined;

      const preset = this.globalPresets.find(x => x.preset.id === presetId);

      const customTypographyInputValue: CustomTypographyInputValue = {
        customTypography: preset?.custom?.customTypography
          ? preset?.custom?.customTypography
          : {}
      };

      if (!preset) {
        customTypographyInputValue.customTypography =
          "previousCustom" in this.value
            ? this.value.previousCustom?.customTypography
              ? this.value.previousCustom?.customTypography
              : {}
            : customTypographyInputValue.customTypography;
      }

      this.emit({
        custom: {
          customTypography: customTypographyInputValue.customTypography
        }
      });
    }

    if (previousMode === "custom" && newMode === "global") {
      this.emit({
        preset: undefined,
        previousCustom: "custom" in this.value ? this.value.custom : undefined
      });
    }
  }

  updatePreset(newPresetId: string) {
    this.emit({
      ...this.value,
      preset: newPresetId
    });
  }

  updateCustom(
    newCustom:
      | CustomTypographyInputValue
      | ValueEmittedFromControlInputDecoratedWithMetadata<CustomTypographyInputValue>
  ) {
    const newCustomValue: CustomTypographyInputValue =
      newCustom instanceof ValueEmittedFromControlInputDecoratedWithMetadata
        ? newCustom.value
        : newCustom;

    const newTypographyValue: TypographyInputValue = {
      ...this.value,
      custom: newCustomValue
    };

    // Inner input has emitted some metadata alongside with a value
    if (
      newCustom instanceof ValueEmittedFromControlInputDecoratedWithMetadata
    ) {
      const valueDecoratedWithMetadata =
        decorateValueEmittedByInputWithMetadata(
          newTypographyValue,
          newCustom.meta
        );

      this.emit(valueDecoratedWithMetadata);
    } else {
      this.emit(newTypographyValue);
    }
  }

  managePresets() {
    setGlobalSettingsAccordionState("settings.typography", true);
    openUiPanel("globalSettings");
  }

  get globalPresets(): TypographyPreset[] {
    return globalStore.typographyPresets || [];
  }
}
